"use client";

import { useEffect } from "react";

import { pushEvent, type LinkType } from "@/lib/analytics";

function classifyLink(
  href: string,
  anchor: HTMLAnchorElement,
): LinkType | null {
  if (href.startsWith("mailto:")) return "email";
  if (href.startsWith("tel:")) return "phone";
  if (anchor.hasAttribute("download")) return "download";

  try {
    const url = new URL(href, window.location.origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (url.origin !== window.location.origin) return "outbound";
  } catch {
    return null;
  }

  // Internal navigation is already captured by page_view.
  return null;
}

export function LinkClickTracker(): null {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href) return;

      const linkType = classifyLink(href, anchor);
      if (!linkType) return;

      const label = (anchor.textContent ?? "").trim().slice(0, 100);

      pushEvent({
        event: "cta_click",
        label: label || href,
        link_url: href,
        link_type: linkType,
      });
    };

    document.addEventListener("click", handleClick, { capture: true });
    return () =>
      document.removeEventListener("click", handleClick, { capture: true });
  }, []);

  return null;
}
